import { useMemo } from "react"
import { usePackageAssignmentQuery, usePackageQuery } from "@pathwright/graphql-client/src/generated"
import { PackageDataSource } from "./types"
import { getPackageParams, mapUrlParamsToVariables } from "./utils"

type UsePackageOptions = {
  searchParams?: URLSearchParams
  dataSource?: PackageDataSource
}

const hasResolvedPackageParams = (urlParams: URLSearchParams) => {
  for (const value of urlParams.values()) {
    if (value?.trim()) return true
  }

  return false
}

// We can determine a package by URL params or by its assignment to a school.
// Depending on the context, we may only want assigned packages or url param packages.
// If no data source is provided, we try both: URL first, then assignment
const getShouldUseAssignmentQuery = (
  urlParams: URLSearchParams,
  dataSource?: PackageDataSource
) => {
  return (
    dataSource === "assignment" ||
    (!dataSource && !hasResolvedPackageParams(urlParams))
  )
}

export const usePackage = (options: UsePackageOptions = {}) => {
  const { searchParams, dataSource } = options

  const urlParams = useMemo(
    () => getPackageParams(searchParams),
    [searchParams]
  )

  const shouldUseAssignmentQuery = useMemo(
    () => getShouldUseAssignmentQuery(urlParams, dataSource),
    [urlParams, dataSource]
  )

  const variables = useMemo(
    () => mapUrlParamsToVariables(urlParams),
    [urlParams]
  )

  const packageQuery = usePackageQuery({
    variables,
    skip: shouldUseAssignmentQuery
  })
  const packageAssignmentQuery = usePackageAssignmentQuery({
    skip: !shouldUseAssignmentQuery
  })
  const packageData = shouldUseAssignmentQuery
    ? packageAssignmentQuery.data?.packageAssignment?.package || null
    : packageQuery.data?.package || null
  const loading = shouldUseAssignmentQuery
    ? packageAssignmentQuery.loading
    : packageQuery.loading
  const error = shouldUseAssignmentQuery
    ? packageAssignmentQuery.error
    : packageQuery.error

  return {
    packageData,
    loading,
    error,
    source: shouldUseAssignmentQuery ? "assignment" : "params",
    assignment: packageAssignmentQuery.data?.packageAssignment || null
  }
}
